import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Stack,
  Alert,
  IconButton,
  Snackbar,
  CircularProgress,
  Chip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { supabase } from './supabaseClient';
import useSession from '../hooks/useSession';

const ApiKeyManager = () => {
  const session = useSession();
  const [apiKeyData, setApiKeyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    if (session?.user?.id) {
      console.log('Session user ID:', session.user.id);
      fetchApiKey();
    }
  }, [session]);

  const fetchApiKey = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Full session object:', session);
      console.log('Session user:', session.user);
      console.log('Session user ID:', session.user.id);
      console.log('Session user email:', session.user.email);
      
      // Also get the current user from Supabase auth
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current auth user:', user);
      console.log('Current auth user ID:', user?.id);
      
      const userIdToQuery = session.user.id || user?.id;
      console.log('Using user ID for query:', userIdToQuery);
      
      // Direct query to api_keys table
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('supabase_user_id', userIdToQuery)
        .eq('is_active', true)
        .maybeSingle(); // Use maybeSingle instead of single to avoid error when no rows
      
      console.log('API key query result:', { data, error });
      
      if (error) {
        throw error;
      }
      
      setApiKeyData(data);
    } catch (err) {
      console.error('Error fetching API key:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Typography variant="body1" sx={{ mb: 1 }}>
        API Key
      </Typography>
      <Paper
        sx={(theme) => ({
          p: 3,
          bgcolor:
            theme.palette.mode === 'light'
              ? theme.palette.background.neutral
              : theme.palette.background.paper,
        })}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : apiKeyData ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                API Key
              </Typography>
              <Box sx={{ 
                fontFamily: 'monospace', 
                bgcolor: 'background.paper',
                p: 2,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all',
                    fontSize: '0.875rem',
                    opacity: showKey ? 1 : 0.7
                  }}
                >
                  {showKey ? apiKeyData.key_hash : '••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••••'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
                  <IconButton
                    size="small"
                    onClick={() => setShowKey(!showKey)}
                    title={showKey ? "Hide key" : "Show key"}
                  >
                    {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(apiKeyData.key_hash)}
                    title="Copy to clipboard"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                Status
              </Typography>
              <Chip 
                label={apiKeyData.is_active ? 'Active' : 'Inactive'} 
                color={apiKeyData.is_active ? 'success' : 'default'}
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                Tier
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {apiKeyData.tier || 'Free'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                Rate Limit
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {apiKeyData.rate_limit} requests/hour
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                Created
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(apiKeyData.created_at)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body1">
                Last Used
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(apiKeyData.last_used_at)}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No API key found for your account.
          </Typography>
        )}
      </Paper>
      
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="API key copied to clipboard"
      />
    </>
  );
};

export default ApiKeyManager;